import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CmsSearchParameters,
  CmsService,
  formatArticleBlock,
} from '@domgen/dgx-fe-cms';
import { ArticleBlock } from '@domgen/dgx-fe-components/content';
import { VideoBlock } from '@domgen/dgx-fe-components/media';
import { ComponentStore } from '@ngrx/component-store';
import { CmsArticleSearchPage } from '@self-fix/util-models';
import { Observable, map, tap } from 'rxjs';
import { AppEnvironment } from 'apps/dg-self-fix/src/app/models/environment/app-environment.type';
import { ENVIRONMENT } from '@domgen/dgx-fe-config';

interface ComponentState {
  articles: ArticleBlock[];
  videos: VideoBlock[];
  quantity: number;
  selectedArticles: ArticleBlock[];
  selectedCategories: string[];
  selectedSubcategories: string[];
  selectedVideos: VideoBlock[];
}

@Injectable({
  providedIn: 'root',
})
export class ArticleSearchService extends ComponentStore<ComponentState> {
  readonly quantity$ = this.select((state: ComponentState) => state.quantity);

  readonly selectedArticles$ = this.select(
    (state: ComponentState) => state.selectedArticles
  );

  readonly selectedCategories$ = this.select(
    (state: ComponentState) => state.selectedCategories
  );

  readonly selectedSubcategories$ = this.select(
    (state: ComponentState) => state.selectedSubcategories
  );

  readonly selectedVideos$ = this.select(
    (state: ComponentState) => state.selectedVideos
  );

  readonly articles$ = this.select((state: ComponentState) => state.articles);

  readonly videos$ = this.select((state: ComponentState) => state.videos);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cmsService: CmsService,
    @Inject(ENVIRONMENT) private readonly environment: AppEnvironment
  ) {
    super({
      articles: [],
      videos: [],
      quantity: 0,
      selectedArticles: [],
      selectedCategories: [],
      selectedSubcategories: [],
      selectedVideos: [],
    });
  }

  readonly searchTerm$: Observable<string | undefined> =
    this.route.queryParamMap.pipe(
      map((queryParamMap) => queryParamMap.get('term') ?? undefined)
    );

  readonly updateQuantity = this.updater(
    (state: ComponentState, quantity: number) => ({
      ...state,
      quantity,
    })
  );

  readonly updateSelectedArticles = this.updater(
    (state: ComponentState, selectedArticles: ArticleBlock[]) => ({
      ...state,
      selectedArticles,
    })
  );

  readonly updateSelectedCategories = this.updater(
    (state: ComponentState, selectedCategories: string[]) => ({
      ...state,
      selectedCategories,
    })
  );

  readonly updateSelectedSubcategories = this.updater(
    (state: ComponentState, selectedSubcategories: string[]) => ({
      ...state,
      selectedSubcategories,
    })
  );

  readonly updateSelectedVideos = this.updater(
    (state: ComponentState, selectedVideos: VideoBlock[]) => ({
      ...state,
      selectedVideos,
    })
  );

  readonly updateArticles = this.updater(
    (state: ComponentState, articles: ArticleBlock[]) => ({
      ...state,
      articles,
    })
  );

  private search(searchTerm: string, limit?: number) {
    const supportedAppliances = this.environment.brandConfig.appliances;
    let applianceCode;
    if (supportedAppliances && supportedAppliances.length > 0) {
      applianceCode =
        supportedAppliances.join('|') +
        '|' +
        supportedAppliances.join(' |') +
        ' |';
    }
    const parameters: CmsSearchParameters = {
      limit: limit ?? 50,
      orderBy: 'ASC',
      q: searchTerm,
    };
    if (applianceCode) {
      parameters['applianceCode'] = applianceCode;
    }
    return this.cmsService.search<CmsArticleSearchPage>(parameters);
  }

  getArticles(searchTerm: string, limit?: number): Observable<ArticleBlock[]> {
    return this.search(searchTerm, limit).pipe(
      tap(console.log),
      map((articles) =>
        formatArticleBlock(
          articles.results.filter(
            (item) => item?.category?.['@name'] === 'articles'
          )
        )
      ),
      tap((formattedArticles) => this.updateArticles(formattedArticles)),
      tap((formattedArticles) => this.updateQuantity(formattedArticles.length))
    );
  }

  getVideos(searchTerm: string, limit?: number): Observable<VideoBlock[]> {
    return this.search(searchTerm, limit).pipe(
      map((results) => {
        // Filter for items that have 'videos' category and format them as videos
        return results.results
          .filter((item) => item?.category?.['@name'] === 'videos')
          .map((video) => ({
            title: video.articleTitle || video['@name'],
            body: video.introText || '',
            videoId: (video as any).videoId || '',
            navigationLink: {
              href: video['@path']?.replace(/\/([A-Za-z-]+\/){3}/, '') || '',
              label: video.articleTitle || video['@name'],
            },
          }));
      }),

      tap((formattedVideos) => this.updateVideos(formattedVideos)),
      tap((formattedVideos) => this.updateSelectedVideos(formattedVideos))
    );
  }

  getRandomVideos(): Observable<VideoBlock[]> {
    return this.getVideos('', 50).pipe(
      map((videos) => {
        const shuffled = videos.sort(() => 0.5 - Math.random());
        return shuffled.slice(0, 3);
      })
    );
  }

  formatPopularVideos(appliance: any): VideoBlock[] {
    if (!appliance?.popularVideosBlock) return [];

    return appliance.popularVideosBlock.popularVideos.map((video: any) => ({
      title: video.popularVideo.articleTitle,
      body: video.popularVideo.introText || '',
      videoId: video.popularVideo.videoId || '',
      navigationLink: {
        href: video.popularVideo['@path'].replace(/\/([A-Za-z-]+\/){3}/, ''),
        label: video.popularVideo.articleTitle,
      },
    }));
  }

  readonly updateVideos = this.updater(
    (state: ComponentState, videos: VideoBlock[]) => ({
      ...state,
      videos,
    })
  );
}
